import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MultiStepWizard } from 'components/Wizards/MultiStepWizard/MultiStepWizard'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { CTA } from 'stores/OnboardingStore'
import { useRootStore } from 'stores/RootStore'
import { LinkwithIds, MultiStepBlogPostWizardProps } from './Types'
import { object } from 'yup'
import { filename } from 'components/Wizards/schemas/shared/filename'
import { project } from 'components/Wizards/schemas/shared/project'
import { StepSchema } from 'components/Wizards/schemas/Types'
import { SelectTitle } from 'components/Wizards/MultiStepBlogPostWizard/SelectTitle'
import { SelectOutline } from './SelectOutline'
import { TitleOptionsProvider, useTitleOptions } from 'components/Wizards/MultiStepBlogPostWizard/TitleOptionsProvider'
import { OutlineProvider, useOutline } from 'components/Wizards/MultiStepBlogPostWizard/OutlineProvider'
import { SelectImages } from './SelectImages'
import { MultiStepWizardProps } from 'components/Wizards/MultiStepWizard/Types'
import { ImagesProvider } from 'components/Wizards/SelectImages/ImagesProvider'
import { FirstStep } from './FirstStep'
import { SelectLinks } from './SelectLinks'
import { v4 as uuidv4 } from 'uuid'
import { LinksProvider } from './LinksProvider'
import { useState } from 'react'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'

const extractValidUrls = (links: LinkwithIds[]) => {
  return links.map((link) => link.url).filter((url) => url !== '')
}

const MultiStepBlogPostWizard = ({
  onSuccess,
  nodeSelection,
  articleId,
  layerId,
  seasonalPromotion,
  isTurnInto,
  doNotIncludeImagesDefault,
  ...props
}: MultiStepBlogPostWizardProps & Pick<MultiStepWizardProps, 'onSubmit'>) => {
  const { introduction, outline } = useOutline()
  const { titleOptions } = useTitleOptions()
  const [initialId] = useState<string>(uuidv4())
  return (
    <MultiStepWizard.Root {...props}>
      <FirstStep />
      <SelectTitle initialValues={{ selected_title_id: titleOptions[0]?.[0] }} />
      <SelectOutline initialValues={{ introduction, outline }} />
      <SelectLinks
        initialValues={{
          external_links: [{ id: initialId, url: '' }],
        }}
      />
      <SelectImages doNotIncludeImagesDefault={doNotIncludeImagesDefault} />
    </MultiStepWizard.Root>
  )
}

const MultiStepBlogPostWizardContainer = ({
  onSuccess,
  nodeSelection,
  articleId,
  layerId,
  seasonalPromotion,
  isTurnInto,
  schema,
  ...props
}: MultiStepBlogPostWizardProps) => {
  const sourceMaterials = useSourceMaterials()
  const { onboardingStore } = useRootStore()
  const isCreatingNewFile = !articleId
  const multiStepSchema = { ...schema }
  if (isCreatingNewFile) {
    multiStepSchema.steps = [
      multiStepSchema.steps[0].concat(object({ filename, project })) as StepSchema,
      ...multiStepSchema.steps.slice(1),
    ]
  }

  const { data: brandProfile } = useBrandProfile()
  const brandKit = brandProfile?.brand_kit

  const doNotIncludeImagesDefault = !(brandKit?.content_preferences?.blog_always_include_images ?? true)

  const { generateContent } = useGenerateContent({
    generationType: ChannelType.BlogPost,
    articleId,
    layerId,
    nodeSelection,
    promptLibrary: seasonalPromotion === 'black_friday' ? 'black_friday' : 'default',
    isTurnInto,
    onSuccess,
  })

  const handleSubmit = (values: any) => {
    const referenceText = (
      values.source_materials
        ? sourceMaterials?.data?.filter(({ id }) => values.source_materials?.includes(id)) || []
        : []
    )
      .map(({ content }) => content)
      .join('\n')

    const { outline, external_links, generated_links, include_images, ...submitValues } = values

    const fields = {
      generation_type: ChannelType.BlogPost,
      ...submitValues,
      format: outline,
      reference_text: referenceText.length > 0 ? referenceText : undefined,
      external_links: extractValidUrls([...(external_links ?? []), ...(generated_links ?? [])]),
      include_images: include_images ?? !doNotIncludeImagesDefault,
    }

    onboardingStore.markCTAAsCompleted(CTA.BlogPost)
    return generateContent(fields)
  }

  return (
    <ImagesProvider initialMaxPhotos={6} orientation="landscape">
      <TitleOptionsProvider>
        <OutlineProvider>
          <LinksProvider>
            <MultiStepBlogPostWizard
              {...props}
              onSubmit={(values) => handleSubmit(values)}
              schema={multiStepSchema}
              doNotIncludeImagesDefault={doNotIncludeImagesDefault}
            />
          </LinksProvider>
        </OutlineProvider>
      </TitleOptionsProvider>
    </ImagesProvider>
  )
}

export { MultiStepBlogPostWizardContainer as MultiStepBlogPostWizard }
